import { useState, useCallback, useContext } from 'react'
import { useUsers, useSetores, useUsersFromMySetores, useMeusSetores } from './useUserQueries'
import {
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useVincularUsuariosMutation,
  useDesvincularUsuarioMutation,
} from './useUserMutations'
import { AuthContext } from '../../../contexts/AuthContext'
import type { UserFilters, CreateUserRequest, UpdateUserRequest } from '../types'
import type { UserFilters as ServiceUserFilters } from '../../../services/usuario'

export const useUsersManagement = () => {
  const auth = useContext(AuthContext)
  const currentUser = auth?.user

  // Verificar se é admin
  const isAdmin = currentUser?.funcao_usuario === 'ADMIN' || currentUser?.funcao_usuario === 'DIRETOR'

  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    setor: '',
    funcao: 'ALL',
    status: 'active'
  })

  // Construir filtros para enviar ao servidor (busca apenas com 3+ caracteres)
  const serverFilters: ServiceUserFilters = {}
  if (filters.search && filters.search.length >= 3) serverFilters.buscar = filters.search
  if (filters.setor && filters.setor !== '') serverFilters.setor = filters.setor
  if (filters.funcao && filters.funcao !== 'ALL') serverFilters.funcao = filters.funcao

  // Query para buscar dados - usa rota diferente baseado em permissões
  // Apenas uma das queries será executada baseado no isAdmin
  const allUsersQuery = useUsers(serverFilters, isAdmin)
  const mySetoresUsersQuery = useUsersFromMySetores(!isAdmin)

  // Selecionar a query apropriada
  const usersQuery = isAdmin ? allUsersQuery : mySetoresUsersQuery
  const { data: usersData = [], isLoading: loading, refetch: refetchUsers } = usersQuery
  
  // Query de setores - usar rota diferente baseado em permissões
  const allSetoresQuery = useSetores(isAdmin)
  const meusSetoresQuery = useMeusSetores(!isAdmin)
  const setoresQuery = isAdmin ? allSetoresQuery : meusSetoresQuery
  const { data: setores = [], isLoading: loadingSetores, refetch: refetchSetores } = setoresQuery

  // Mutations
  const createUserMutation = useCreateUserMutation()
  const updateUserMutation = useUpdateUserMutation()
  const deleteUserMutation = useDeleteUserMutation()
  const vincularUsuariosMutation = useVincularUsuariosMutation()
  const desvincularUsuarioMutation = useDesvincularUsuarioMutation()

  // Aplicar filtros client-side para não-admins (já que a API não aceita filtros nessa rota)
  const users = isAdmin ? usersData : usersData.filter(user => {
    // Filtrar por busca
    if (filters.search && filters.search.length >= 3) {
      const search = filters.search.toLowerCase()
      const matchName = user.name?.toLowerCase().includes(search)
      const matchEmail = user.email?.toLowerCase().includes(search)
      if (!matchName && !matchEmail) return false
    }

    // Filtrar por setor
    if (filters.setor && filters.setor !== '') {
      const hasSetor = user.setores?.some(s => s.id.toString() === filters.setor)
      if (!hasSetor) return false
    }

    // Filtrar por função
    if (filters.funcao && filters.funcao !== 'ALL') {
      if (user.funcao_usuario !== filters.funcao) return false
    }

    return true
  })

  // Create user
  const createUser = useCallback(async (data: CreateUserRequest): Promise<boolean> => {
    try {
      await createUserMutation.mutateAsync(data)
      return true
    } catch {
      return false
    }
  }, [createUserMutation])

  // Update user
  const updateUser = useCallback(async (id: number, data: UpdateUserRequest): Promise<boolean> => {
    try {
      await updateUserMutation.mutateAsync({ id, data })
      return true
    } catch {
      return false
    }
  }, [updateUserMutation])

  // Delete user
  const deleteUser = useCallback(async (id: number): Promise<boolean> => {
    try {
      await deleteUserMutation.mutateAsync(id)
      return true
    } catch {
      return false
    }
  }, [deleteUserMutation])

  // Vincular setores a um usuário
  const vincularSetores = useCallback(async (setorId: number, usuariosIds: number[]): Promise<boolean> => {
    try {
      await vincularUsuariosMutation.mutateAsync({ setorId, usuariosIds })
      return true
    } catch {
      return false
    }
  }, [vincularUsuariosMutation])

  // Desvincular setor de um usuário
  const desvincularSetor = useCallback(async (usuarioId: number, setorId: number): Promise<boolean> => {
    try {
      await desvincularUsuarioMutation.mutateAsync({ usuarioId, setorId })
      return true
    } catch {
      return false
    }
  }, [desvincularUsuarioMutation])

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<UserFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }, [])

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      setor: '',
      funcao: 'ALL',
      status: 'active'
    })
  }, [])

  // Refresh data
  const refresh = useCallback(() => {
    refetchUsers()
    refetchSetores()
  }, [refetchUsers, refetchSetores])

  return {
    users, // Dados filtrados
    allUsers: users,
    setores,
    loading,
    loadingSetores,
    filters,
    updateFilters,
    clearFilters,
    createUser,
    updateUser,
    deleteUser,
    vincularSetores,
    desvincularSetor,
    refresh,
    isAdmin, // Exportar para uso na UI
  }
}

